import React from 'react';

// Uplift III Shield Logo Component
// The official emblem for Robert Elder III's brands

export const UpliftLogo = ({ size = 'md', showText = true, className = '' }) => {
  const sizes = {
    sm: { shield: 32, text: 'text-sm' },
    md: { shield: 40, text: 'text-lg' },
    lg: { shield: 56, text: 'text-2xl' },
    xl: { shield: 72, text: 'text-3xl' },
  };

  const { shield, text } = sizes[size] || sizes.md;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Shield SVG */}
      <svg
        width={shield}
        height={shield * 1.15}
        viewBox="0 0 40 46"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="flex-shrink-0"
      >
        {/* Shield Shape */}
        <path
          d="M20 2L4 8V20C4 31.5 11 39.5 20 44C29 39.5 36 31.5 36 20V8L20 2Z"
          fill="url(#shieldGradient)"
          stroke="url(#shieldStroke)"
          strokeWidth="2"
        />
        
        {/* Inner Shield Highlight */}
        <path
          d="M20 6L8 10.5V20C8 29 13.5 35.5 20 39.5C26.5 35.5 32 29 32 20V10.5L20 6Z"
          fill="url(#innerGradient)"
          opacity="0.3"
        />
        
        {/* III Roman Numerals */}
        <text
          x="20"
          y="26"
          textAnchor="middle"
          fill="white"
          fontSize="14"
          fontWeight="bold"
          fontFamily="serif"
          style={{ letterSpacing: '1px' }}
        >
          III
        </text>
        
        {/* Upward Arrow/Chevron */}
        <path
          d="M14 32L20 28L26 32"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        
        {/* Gradients */}
        <defs>
          <linearGradient id="shieldGradient" x1="20" y1="2" x2="20" y2="44" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#4F46E5" />
            <stop offset="50%" stopColor="#6366F1" />
            <stop offset="100%" stopColor="#4338CA" />
          </linearGradient>
          <linearGradient id="shieldStroke" x1="20" y1="2" x2="20" y2="44" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#818CF8" />
            <stop offset="100%" stopColor="#4F46E5" />
          </linearGradient>
          <linearGradient id="innerGradient" x1="20" y1="6" x2="20" y2="39.5" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#FFFFFF" />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
      
      {/* Brand Text */}
      {showText && (
        <div className="flex flex-col leading-none">
          <span 
            className={`font-bold text-slate-900 ${text}`}
            style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.02em' }}
          >
            Uplift
          </span>
          <span className="text-[10px] text-slate-500 uppercase tracking-widest hidden sm:block">
            Career • Business • Credit
          </span>
        </div>
      )}
    </div>
  );
};

// Shield Icon Only (for favicons, app icons)
export const UpliftShield = ({ size = 40, color = '#4F46E5' }) => (
  <svg
    width={size}
    height={size * 1.15}
    viewBox="0 0 40 46"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M20 2L4 8V20C4 31.5 11 39.5 20 44C29 39.5 36 31.5 36 20V8L20 2Z"
      fill={color}
    />
    <text
      x="20"
      y="26"
      textAnchor="middle"
      fill="white"
      fontSize="14"
      fontWeight="bold"
      fontFamily="serif"
      style={{ letterSpacing: '1px' }}
    >
      III
    </text>
    <path
      d="M14 32L20 28L26 32"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

// White version for dark backgrounds
export const UpliftLogoWhite = ({ size = 'md', showText = true, className = '' }) => {
  const sizes = {
    sm: { shield: 32, text: 'text-sm' },
    md: { shield: 40, text: 'text-lg' },
    lg: { shield: 56, text: 'text-2xl' },
    xl: { shield: 72, text: 'text-3xl' },
  };

  const { shield, text } = sizes[size] || sizes.md;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <svg
        width={shield}
        height={shield * 1.15}
        viewBox="0 0 40 46"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="flex-shrink-0"
      >
        <path
          d="M20 2L4 8V20C4 31.5 11 39.5 20 44C29 39.5 36 31.5 36 20V8L20 2Z"
          fill="white"
          fillOpacity="0.2"
          stroke="white"
          strokeWidth="2"
        />
        <text
          x="20"
          y="26"
          textAnchor="middle"
          fill="white"
          fontSize="14"
          fontWeight="bold"
          fontFamily="serif"
          style={{ letterSpacing: '1px' }}
        >
          III
        </text>
        <path
          d="M14 32L20 28L26 32"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
      
      {showText && (
        <div className="flex flex-col leading-none">
          <span 
            className={`font-bold text-white ${text}`}
            style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.02em' }}
          >
            Uplift
          </span>
          <span className="text-[10px] text-white/70 uppercase tracking-widest hidden sm:block">
            Career • Business • Credit
          </span>
        </div>
      )}
    </div>
  );
};

export default UpliftLogo;
